import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Box, Stack, Table, TableHead, TableBody, TableRow, TableCell } from "@mui/material";
import { useNavigate } from "react-router";
import TopBar from "../../../../../component/TopBar";
import MainBox from "../../../container/MainBox";
import DataTable from "../../../../../component/DataTable";
import BtnPrimary from "../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../component/SecondaryButton";
import EmptyState from "../../../../../component/EmptyState";
import InlineMessage from "../../../../../component/InlineMessage";
import Counter from "../../../../../component/Counter";
import { StatusChip, headCellSx, bodyCellSx } from "../../../../../component/listing";
import { counterRowSx } from "../../../container/frameLayout";
import { getSelfTimesheets } from "../../../../../services/timesheet";
import { useNotify } from "../../../../../component/NotificationProvider";
import { tokens, displayFont } from "../../../../../theme";
import {
  currentSheet, weeksOf, weekGrid, monthTotals, awaitingApproval,
  sheetStatus, canSubmit, overtimeNote, weekLabel, weekStart, hours,
} from "./selfTimesheet";

/**
 * The person's own timesheets.
 *
 * `getSelfTimesheets` has existed since timesheets did and was called from
 * nowhere: we built the manager's week and the admin's settings, and the person
 * who actually fills the thing in had no screen at all.
 *
 * The week grid is read-only here. Entering time is the existing form, which
 * knows about projects, the daily cap and required-project rules; duplicating
 * that as an editable grid would be a second definition of what a valid entry
 * is.
 */
const MyTimesheets = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [sheets, setSheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [weekKey, setWeekKey] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await getSelfTimesheets();
      if (res?.success === false) throw new Error(res?.message || "Failed to load your timesheets");
      setSheets(Array.isArray(res?.data) ? res.data : []);
    } catch (e) {
      setFailed(true);
      notify.error(e?.message || "Failed to load your timesheets");
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { load(); }, [load]);

  const weeks = useMemo(() => weeksOf(sheets), [sheets]);
  const sheet = useMemo(
    () => weeks.find((s) => weekStart(s.startDate) === weekKey) || currentSheet(sheets),
    [weeks, weekKey, sheets]
  );
  const grid = useMemo(() => weekGrid(sheet), [sheet]);
  const month = useMemo(() => monthTotals(sheets, sheet?.startDate), [sheets, sheet]);
  const waiting = useMemo(() => awaitingApproval(sheets), [sheets]);
  const note = overtimeNote(sheet);
  const status = sheetStatus(sheet);

  const numCell = { ...bodyCellSx, textAlign: "right", fontVariantNumeric: "tabular-nums" };
  const numHead = { ...headCellSx, textAlign: "right" };

  return (
    <Box sx={{ width: "100%" }}>
      <TopBar MainHeading="Timesheets" />
      <MainBox sx={{ padding: "1.25rem" }}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "flex-start" }} spacing={1.5} sx={{ mb: "1.25rem" }}>
          <Box>
            <Box sx={{ fontFamily: displayFont, fontSize: "1.5rem", fontWeight: 700, color: tokens.text }}>
              {sheet ? weekLabel(weekStart(sheet.startDate)) : "Timesheets"}
            </Box>
            <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, mt: "0.25rem" }}>
              {loading
                ? " "
                : sheet
                ? `${hours(sheet.totalHours)} hours · ${status.label.toLowerCase()}`
                : "You have not logged any time yet."}
            </Box>
          </Box>
          {/* Submitting is the one thing here that cannot be undone — the sheet
              leaves your hands and goes to your approver. */}
          <Stack direction="row" spacing={1}>
            {sheet && (
              <BtnSecondary onClick={() => navigate(`/app/timesheet/${sheet._id}`)} sx={{ borderRadius: "8px" }}>
                Open this week
              </BtnSecondary>
            )}
            {/* Logging time is the one action here that creates something. A
                draft cannot be edited and submitted from any existing screen —
                see the note below — so this starts a new week rather than
                promising something the product cannot do. */}
            <BtnPrimary to="/app/timesheet/new" sx={{ borderRadius: "8px" }}>Log time</BtnPrimary>
          </Stack>
        </Stack>

        <Box sx={{ ...counterRowSx(4), mb: "1.25rem" }}>
          <Counter
            label="Hours this week"
            value={sheet ? hours(sheet.totalHours) : "—"}
            sub={sheet?.overtimeHours > 0 ? `${hours(sheet.overtimeHours)} above the standard week` : "within the standard week"}
          />
          <Counter label="Hours this month" value={hours(month.hours)} sub={`${month.weeks} week${month.weeks === 1 ? "" : "s"} submitted`} />
          <Counter
            label="Waiting on approval"
            value={waiting.length}
            sub={waiting.length ? weekLabel(weekStart(waiting[0].startDate)).toLowerCase() : "nothing pending"}
            tone={waiting.length ? tokens.warningText : undefined}
          />
          <Counter
            label="This week"
            value={status.label}
            sub={sheet ? `${grid.rows.length} project${grid.rows.length === 1 ? "" : "s"}` : "no sheet yet"}
          />
        </Box>

        {note && <InlineMessage tone="warning" sx={{ mb: "1.25rem" }}>{note}</InlineMessage>}

        {/* A draft that cannot be finished is worth saying out loud rather than
            leaving the reader to hunt for the button. */}
        {canSubmit(sheet) && (
          <InlineMessage tone="info" sx={{ mb: "1.25rem" }}>
            This week is still a {sheetStatus(sheet).label.toLowerCase()}. Editing and submitting an
            existing week is not built yet — log the week again if you need to change it.
          </InlineMessage>
        )}

        {/* The week, as a grid: what went where, and what each day came to. */}
        {sheet && grid.days.length > 0 && (
          <Box sx={{ background: tokens.card, border: `1px solid ${tokens.border}`, borderRadius: "10px", overflow: "hidden", mb: "1.25rem" }}>
            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ width: "100%" }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...headCellSx, width: "30%" }}>Project</TableCell>
                    {grid.days.map((d) => (
                      <TableCell key={d.key} sx={numHead}>{d.label}</TableCell>
                    ))}
                    <TableCell sx={{ ...numHead, width: "5.25rem" }}>Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grid.rows.map((r) => (
                    <TableRow key={r.key}>
                      <TableCell sx={{ ...bodyCellSx, fontWeight: 500 }}>{r.name}</TableCell>
                      {grid.days.map((d) => (
                        <TableCell key={d.key} sx={{ ...numCell, color: r.byDay[d.key] ? tokens.text : tokens.textPlaceholder }}>
                          {r.byDay[d.key] ? hours(r.byDay[d.key]) : "—"}
                        </TableCell>
                      ))}
                      <TableCell sx={{ ...numCell, fontWeight: 600 }}>{hours(r.total)}</TableCell>
                    </TableRow>
                  ))}
                  {/* A grid that does not add up is not worth reading. */}
                  <TableRow sx={{ background: tokens.surface }}>
                    <TableCell sx={{ ...bodyCellSx, fontWeight: 700, borderBottom: "none" }}>Daily total</TableCell>
                    {grid.days.map((d) => (
                      <TableCell key={d.key} sx={{ ...numCell, fontWeight: 700, borderBottom: "none" }}>
                        {hours(grid.dailyTotals[d.key])}
                      </TableCell>
                    ))}
                    <TableCell sx={{ ...numCell, fontWeight: 700, borderBottom: "none" }}>{hours(grid.total)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}

        {sheet && grid.days.length === 0 && !loading && (
          <Box sx={{ mb: "1.25rem" }}>
            <EmptyState title="Nothing logged this week" description="Time you log appears here as a grid of projects and days." />
          </Box>
        )}

        <Box sx={{ fontFamily: displayFont, fontSize: "1.0625rem", fontWeight: 700, color: tokens.text, mb: "1rem" }}>
          Your weeks
        </Box>

        <DataTable
          rows={weeks}
          loading={loading}
          error={failed}
          onRetry={load}
          errorTitle="Couldn't load your timesheets"
          empty={{ title: "No timesheets yet", description: "Weeks you log appear here once you start." }}
          onRowClick={(r) => setWeekKey(weekStart(r.startDate))}
          tableSx={{ width: "100%", tableLayout: "fixed" }}
          columns={[
            {
              key: "week",
              label: "Week",
              cellSx: { fontWeight: 500 },
              render: (r) => weekLabel(weekStart(r.startDate)),
            },
            {
              key: "hours",
              label: "Hours",
              align: "right",
              width: "6rem",
              cellSx: { fontVariantNumeric: "tabular-nums" },
              render: (r) => hours(r.totalHours),
            },
            {
              key: "overtime",
              label: "Overtime",
              align: "right",
              width: "6rem",
              render: (r) =>
                r.overtimeHours > 0 ? (
                  <Box component="span" sx={{ color: tokens.warningText, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                    {hours(r.overtimeHours)}
                  </Box>
                ) : (
                  <Box component="span" sx={{ color: tokens.textPlaceholder }}>—</Box>
                ),
            },
            {
              key: "status",
              label: "Status",
              width: "12rem",
              render: (r) => {
                const s = sheetStatus(r);
                return <StatusChip label={s.label} tone={s.tone} />;
              },
            },
          ]}
        />
      </MainBox>
    </Box>
  );
};

export default MyTimesheets;
